import React from 'react';

import { Button, Spinner, Text } from '@change/ds-core-ui';
import { ArrowRightIcon } from '@change/ds-icons';
import { cn } from '@change/ds-utils';

import { Translate, useI18n } from '@change-corgi/core-react/i18n';

import {
	HeroTypingOverlay,
	PromptInput,
	PromptInputActions,
	PromptInputTextarea,
} from '#src/app/pages/homepage/shared/components';
import { useHeroTypingPlaceholder } from '#src/app/pages/homepage/shared/hooks';

import { FormHelper } from './components/FormHelper';
import { LinkChip } from './components/LinkChip';
import { useSocialPostUrlForm } from './hooks/useSocialPostUrlForm';

type SubmitAreaProps = Readonly<{ hasChip: boolean; isSubmitting: boolean; submitAriaLabel: string }>;

const SubmitArea: React.FC<SubmitAreaProps> = ({ hasChip, isSubmitting, submitAriaLabel }) =>
	hasChip ? (
		<PromptInputActions data-qa="prompt-input-actions" className="left-3">
			<Button
				aria-live="polite"
				trailingIcon={<ArrowRightIcon />}
				loading={isSubmitting}
				variant="primary"
				type="submit"
				size="small"
				data-qa="prompt-input-actions-button"
				className="bg-fill-interactive w-full rounded-full"
			>
				<Translate value="corgi.homepage.hero_section.input_prompt_submit_button_generate" />
			</Button>
		</PromptInputActions>
	) : (
		<PromptInputActions data-qa="prompt-input-actions">
			<Button
				aria-label={submitAriaLabel}
				aria-live="polite"
				className="bg-fill-interactive ml-auto rounded-full"
				iconOnly
				size="small"
				leadingIcon={<ArrowRightIcon />}
				loading={isSubmitting}
				variant="primary"
				type="submit"
				data-qa="prompt-input-actions-button"
			/>
		</PromptInputActions>
	);

export const SocialPostUrlForm: React.FC = () => {
	const { translate } = useI18n();
	const {
		data: { isSubmitting, isResolvingPreview, error, chip },
		actions: { onSubmit, onPaste, onInputChange, onRemoveChip },
	} = useSocialPostUrlForm();
	const {
		data: { value: prompt, placeholder, showOverlay, descriptionId },
		actions: { onValueChange: setPrompt },
	} = useHeroTypingPlaceholder();

	const submitAriaLabel = isSubmitting
		? translate('corgi.homepage.hero_section.input_prompt_submit_button_loading')
		: translate('corgi.homepage.hero_section.input_prompt_submit_button');

	let promptContent: React.ReactNode;
	if (chip) {
		promptContent = (
			<LinkChip
				handle={chip.handle}
				title={chip.title}
				image={chip.image}
				onRemove={onRemoveChip}
				data-qa="social-post-chip"
			/>
		);
	} else {
		promptContent = (
			<div className="flex w-full flex-col">
				<PromptInputTextarea
					maxLength={500}
					name="prompt_or_url"
					placeholder={placeholder}
					aria-labelledby="social-post-input-heading"
					aria-describedby={showOverlay ? descriptionId : undefined}
					enterKeyHint="send"
					onPaste={onPaste}
					onChange={onInputChange}
					data-qa="social-post-prompt-textarea"
					className={cn({ 'pb-2': isResolvingPreview })}
				/>
				{showOverlay && <HeroTypingOverlay descriptionId={descriptionId} />}
				{isResolvingPreview && (
					<div className="flex items-center gap-2 px-5 pt-1 pb-16" data-qa="social-post-resolving">
						<Spinner className="size-4" />
						<Text variant="body" size="small" className="text-typography-weak">
							<Translate value="corgi.homepage.hero_section.input_resolving_link_label" />
						</Text>
					</div>
				)}
			</div>
		);
	}

	return (
		<div className="mb-3 flex w-full flex-col items-center justify-center lg:mb-3">
			<div className="flex w-full max-w-[500px] flex-col gap-4">
				<div>
					<Text
						className="text-center"
						variant="body"
						size="large"
						fontWeight="bold"
						as="h2"
						id="social-post-input-heading"
					>
						<Translate value="corgi.homepage.hero_section.input_prompt_label_alt" />
					</Text>
				</div>
				<div className="relative">
					<form onSubmit={onSubmit} data-qa="social-post-url-form" className="relative z-10">
						{/* min-h keeps the box the same height whether the chip's CTA row is present or
						    the textarea's floating icon button is — avoids a layout jump when the preview
						    resolves and the chip replaces the textarea. */}
						<PromptInput
							data-qa="social-post-prompt"
							className="group elevation-low min-h-[208px] flex-col justify-between lg:min-h-[192px]"
							value={prompt}
							onValueChange={setPrompt}
						>
							{promptContent}
							<SubmitArea hasChip={!!chip} isSubmitting={isSubmitting} submitAriaLabel={submitAriaLabel} />
						</PromptInput>
					</form>

					<FormHelper error={error} />
				</div>
			</div>
		</div>
	);
};
